import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, ExternalLink, KeyRound, MailCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { AgentMailbox } from "@/types/agent-mailboxes";
import { copyToClipboard, EmailForwardingSteps } from "./EmailForwardingSteps";

const VERIFICATION_FRESH_MS = 3 * 24 * 60 * 60 * 1000;

interface ListenerEmailForwardingProps {
  agentId: string;
  mailbox: AgentMailbox;
}

/**
 * Setup for a Listener genie's listen-only forwarding address: the Gmail
 * confirmation code as soon as Gmail sends it (webhooks/mail-listen.ts
 * broadcast), and per-provider forwarding steps.
 */
export const ListenerEmailForwarding = ({ agentId, mailbox }: ListenerEmailForwardingProps) => {
  const queryClient = useQueryClient();
  const address = mailbox.email_address ?? "";
  const verification = mailbox.forwarding_verification;
  const listenedInboxes = mailbox.listened_inboxes ?? [];
  const showVerification =
    !!verification?.code && Date.now() - new Date(verification.received_at).getTime() < VERIFICATION_FRESH_MS;

  useEffect(() => {
    const channel = supabase
      .channel(`agent-mailbox-${agentId}`)
      .on("broadcast", { event: "mailbox.forwarding_verification" }, () => {
        toast.success("Gmail sent your confirmation code");
        queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
      })
      .subscribe();
    return () => {
      channel.unsubscribe();
    };
  }, [agentId, queryClient]);

  return (
    <div className="space-y-4">
      {showVerification && verification && (
        <div className="rounded-xl border border-genie-primary/30 bg-gradient-to-r from-genie-primary/5 to-genie-secondary/5 p-4">
          <div className="flex items-start gap-3">
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-genie-primary" />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm font-semibold text-gray-900">Gmail confirmation code</p>
              <p className="text-xs text-gray-600">
                {verification.requested_by ?? "Gmail"} asked to forward email here. Enter this code in Gmail's
                forwarding settings and click Verify.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => copyToClipboard(verification.code!, "Code")}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 font-mono text-lg font-semibold tracking-widest text-gray-900 sm:min-h-0 sm:py-1.5"
                >
                  {verification.code}
                  <Copy className="h-4 w-4 text-gray-400" />
                </button>
                {verification.confirmation_url && (
                  <a
                    href={verification.confirmation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[44px] items-center justify-center gap-1.5 text-sm font-semibold text-genie-primary hover:underline sm:min-h-0"
                  >
                    Or confirm in Gmail
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {listenedInboxes.length > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-genie-primary/20 bg-genie-primary/5 px-4 py-3">
          <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-genie-primary" />
          <p className="text-sm text-gray-700">
            Listening to email forwarded from{" "}
            <span className="font-medium text-gray-900">{listenedInboxes.join(", ")}</span>.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-sm font-semibold text-gray-900">
          {listenedInboxes.length > 0 ? "Forward another inbox here" : "Forward your support inbox here"}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          Nothing to connect or log in to. Turn forwarding on in your own email settings, and off again any time.
        </p>

        <EmailForwardingSteps agentId={agentId} address={address} mode="listen" />
      </div>

      <div className="flex items-start gap-2 text-xs text-gray-500">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-genie-secondary" />
        <p>
          Your genie never replies on its own. Each email lands in your Inbox with a summary, how urgent it is, a
          suggested reply from your knowledge base and any matching resources.
        </p>
      </div>
    </div>
  );
};
